import eventService from '../services/eventService';

/**
 * Record that someone tapped the ticket link on an external-ticketing event.
 *
 * External events sell off-platform, so the tap on TICKETS is the only signal we
 * ever get that a guest cared. It writes a no-payment `external_interest`
 * application, which is what puts the event on their profile, surfaces them on
 * the host's interested roster, feeds "friends interested" on the People tab,
 * and subscribes them to change/cancellation notices for that event.
 *
 * That used to happen only on the event detail page, so the same tap from the
 * Featured wall or any list recorded nothing — the majority of taps, since most
 * people never open the detail page first.
 *
 * Fire-and-forget by design. The caller is a real <a target="_blank"> and must
 * navigate whether or not this succeeds: never await it, never preventDefault
 * for it, and never surface its failure. Losing an interest row is a smaller
 * harm than eating someone's click on the way to buying a ticket.
 */

// Per-page-load guard. The server call is idempotent (it reactivates a withdrawn
// row rather than duplicating), so this is only to stop a double-tap sending two.
const sent = new Set();

export function recordExternalInterest(eventId) {
  if (!eventId || sent.has(eventId)) return;

  // Anonymous visitors can browse and buy, but there is no account to attach the
  // interest to. Checking here rather than letting it 401 keeps the console clean
  // and avoids a pointless round trip on every logged-out ticket tap.
  let signedIn = false;
  try {
    signedIn = !!localStorage.getItem('accessToken');
  } catch {
    signedIn = false; // storage disabled — treat as logged out
  }
  if (!signedIn) return;

  sent.add(eventId);
  Promise.resolve()
    .then(() => eventService.registerExternalInterest(eventId))
    .catch(() => {
      // Swallowed on purpose: they are already on their way to the ticket page.
      // Drop it from the guard so a later tap in the same session can retry.
      sent.delete(eventId);
    });
}

export default recordExternalInterest;
